import request from '@/api/request'
import { UserFeatures } from '@/store/user/constants'
import { ApiResponse } from '@gry-dmitrij/api-request'
import { TypeLang } from '@/store/language/language'
import { CurrencyCode } from '@/store/currency/currency'

export type SubscriptionCode =
  | 'free'
  | 'package_basic'
  | 'package_core'
  | 'package_power'
  | 'package_max'
  | 'package_ultra'
  | 'package_membership'

interface ISubscriptionFeaturesApi {
  biometric_security: boolean
  broadcasting: boolean
  custom_themes: boolean
  translation: boolean
  voice_to_text: boolean
}

interface ISubscriptionLimitsApi {
  max_audio_seconds: number
  max_file_size: number
  max_group_participants: number
  max_pinned_chats: number
  max_sticker_packs: number
}

interface ISubscriptionApi {
  id: number
  code: SubscriptionCode
  period_days: number
  price: Record<CurrencyCode, number>
  limits: ISubscriptionLimitsApi
  features: ISubscriptionFeaturesApi
  translations: Record<TypeLang, ISubscriptionTranslation>
  is_purchased: boolean
  can_purchase: boolean
}

interface ISubscriptionsResponse {
  subscriptions: ISubscriptionApi[]
}

export interface ISubscriptionTranslation {
  name: string
  description: string
  details: string[]
}

export interface ISubscriptionLimits {
  maxAudioSeconds: number
  maxFileSize: number
  maxGroupParticipants: number
  maxPinnedChats: number
  maxStickerPacks: number
}

export interface ISubscriptionPackage {
  id: number
  code: SubscriptionCode
  periodDays: number
  price: Record<CurrencyCode, number>
  limits: ISubscriptionLimits
  features: Record<UserFeatures, boolean>
  translations: Record<TypeLang, ISubscriptionTranslation>
  isPurchased: boolean
  canPurchase: boolean
}

const mapSubscription = (subscription: ISubscriptionApi): ISubscriptionPackage => ({
  id: subscription.id,
  code: subscription.code,
  periodDays: subscription.period_days,
  price: subscription.price,
  limits: {
    maxAudioSeconds: subscription.limits.max_audio_seconds,
    maxFileSize: subscription.limits.max_file_size,
    maxGroupParticipants: subscription.limits.max_group_participants,
    maxPinnedChats: subscription.limits.max_pinned_chats,
    maxStickerPacks: subscription.limits.max_sticker_packs
  },
  features: {
    [UserFeatures.BiometricSecurity]: subscription.features.biometric_security,
    [UserFeatures.Broadcasting]: subscription.features.broadcasting,
    [UserFeatures.CustomThemes]: subscription.features.custom_themes,
    [UserFeatures.Translation]: subscription.features.translation,
    [UserFeatures.VoiceToText]: subscription.features.voice_to_text
  },
  translations: subscription.translations,
  isPurchased: subscription.is_purchased,
  canPurchase: subscription.can_purchase
})

export const getSubscriptions = async(): Promise<ISubscriptionPackage[]> => {
  const { data }: ApiResponse<ISubscriptionsResponse> = await request('get', '/api/v3/subscriptions')
  return data.subscriptions.map(mapSubscription)
}
